'use client';

import { useState } from 'react';
import { capture } from '@/lib/analytics/posthog';

interface CLIInstallProps {
  variant?: 'default' | 'large' | 'compact' | 'brutalist';
  className?: string;
  location?: string;
}

export function CLIInstall({ variant = 'default', className = '', location }: CLIInstallProps) {
  const [copied, setCopied] = useState(false);
  const command = 'npx @getverbal/cli init';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
    } catch {
      // Fallback for HTTP or denied permissions
      const textarea = document.createElement('textarea');
      textarea.value = command;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
    }
    capture('cta_clicked', { location: location ?? 'unknown', cta: 'cli_install' });
    setTimeout(() => setCopied(false), 2000);
  };

  const isBrutalist = variant === 'brutalist';

  const sizeClasses = {
    compact: 'px-3 py-2 text-xs',
    default: 'px-4 py-3 text-sm',
    large: 'px-6 py-4 text-base',
    brutalist: 'px-6 py-4 text-base',
  };

  if (isBrutalist) {
    return (
      <div
        className={`group relative flex items-center justify-between gap-4 border-4 border-foreground bg-ink ${sizeClasses.brutalist} ${className}`}
        style={{ boxShadow: '6px 6px 0 var(--primary)' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-primary">$</span>
          <code className="font-mono text-primary-foreground">{command}</code>
        </div>
        <button
          onClick={handleCopy}
          aria-label="Copy install command to clipboard"
          className="flex shrink-0 items-center gap-1.5 bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {copied ? (
            <>
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Copied
            </>
          ) : (
            <>
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
              Copy
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`group relative flex items-center justify-between gap-4 rounded-lg border border-[--color-rule] bg-[--color-paper] transition-[border-color] hover:border-primary/50 ${sizeClasses[variant]} ${className}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-primary">$</span>
        <code className="font-mono text-[--color-ink]">{command}</code>
      </div>
      <button
        onClick={handleCopy}
        aria-label="Copy install command to clipboard"
        className="flex shrink-0 items-center gap-1.5 rounded-md bg-secondary px-3 py-1.5 text-xs font-medium text-secondary-foreground transition-[background-color,color] hover:bg-primary hover:text-primary-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {copied ? (
          <>
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Copied
          </>
        ) : (
          <>
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
            Copy
          </>
        )}
      </button>
    </div>
  );
}
